import { Box, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { BUSINESS_VARIANT, ENTERPRISE_VARIANT } from 'modules/onboarding/lib/pricing/plans/utils'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import type { Feature } from 'modules/upsell/lib/selfUpsell/types'

type FeatureWithTooltipProps = {
  title: string
  tooltip: string
}

const FeatureWithTooltip = ({ title, tooltip }: FeatureWithTooltipProps) => {
  const [open, toggle] = useToggle(false)

  return (
    <Tooltip.Alt
      label={tooltip}
      attachment="bottom left"
      targetAttachment="top center"
      open={open}
      toggle={toggle}
    >
      <Text.Normal size="200" dottedUnderline={!!tooltip}>
        {title}
      </Text.Normal>
    </Tooltip.Alt>
  )
}

type FeaturesListProps = {
  addon: Plan
  selectedVariant: string
}

const FeaturesList = ({ addon, selectedVariant }: FeaturesListProps) => {
  const businessFeatures = addon.getFeatures(BUSINESS_VARIANT)
  const enterpriseFeatures = addon.getFeatures(ENTERPRISE_VARIANT)
  const hasEnterpriseVariant = addon.variantTypes.includes(ENTERPRISE_VARIANT)

  return (
    <Box gap="s16" paddingBottom="s16">
      {selectedVariant === ENTERPRISE_VARIANT &&
        hasEnterpriseVariant &&
        enterpriseFeatures.length > 0 && (
          <Box gap="s12">
            <Text.Normal size="100" color="grey800" weight="medium">
              {ENTERPRISE_VARIANT}{' '}
              {i18n.t('self_upsell.common.included_features', {
                variant: BUSINESS_VARIANT,
              })}
            </Text.Normal>
            <Box gap="s12">
              {enterpriseFeatures.map((feature: Feature, index: number) => (
                <Box
                  key={`enterprise-${index}`}
                  flexDirection="row"
                  alignItems="flexStart"
                  cursor={feature.tooltip ? 'pointer' : 'default'}
                >
                  <Box paddingRight="s4" paddingTop="s2" style={{ flexShrink: '0' }}>
                    <Icon.Small icon="check" color="olive1000" />
                  </Box>
                  {feature.tooltip ? (
                    <FeatureWithTooltip title={feature.title} tooltip={feature.tooltip} />
                  ) : (
                    <Text.Normal size="200">{feature.title}</Text.Normal>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      {businessFeatures.length > 0 && (
        <Box gap="s12">
          <Text.Normal size="100" color="grey800" weight="medium">
            {BUSINESS_VARIANT}
          </Text.Normal>
          <Box gap="s12">
            {businessFeatures.map((feature: Feature, index: number) => (
              <Box
                key={`business-${index}`}
                flexDirection="row"
                alignItems="flexStart"
                cursor={feature.tooltip ? 'pointer' : 'default'}
              >
                <Box paddingRight="s4" paddingTop="s2" style={{ flexShrink: '0' }}>
                  <Icon.Small icon="check" color="olive1000" />
                </Box>
                {feature.tooltip ? (
                  <FeatureWithTooltip title={feature.title} tooltip={feature.tooltip} />
                ) : (
                  <Text.Normal size="200">{feature.title}</Text.Normal>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default FeaturesList
